import type { LucideIcon } from "lucide-react";
import { Compass, FileQuestion, FileText, Globe, Heart, Trophy } from "lucide-react";

export interface BrainFile {
    id: string;
    name: string;
    icon: LucideIcon;
    size: string;
    modified: string;
    kind: "tabs" | "static" | "final-egg";
    curiosityLocked?: boolean;
    paragraphs: string[];
    note?: string;
}

const ORIGIN_STORY = [
    "i can't point to one moment where software 'clicked'. i can point to a tab.",
    "",
    "childhood me collected activities the way i now collect browser tabs: swimming, karate, carnatic music, bharatanatyam, western dance, drawing, badminton, yakshagana. eight side quests, zero regrets. somewhere in there i learned the pattern that still runs my life: try the thing, be delightfully bad at it, keep the interesting part forever.",
    "",
    "engineering at NMAM Institute of Technology (2021–2025, CGPA 8.93/10) gave curiosity a compile step. i went in for computer science and stayed for the questions that don't come with answer keys: what if a document could read itself? what if a model could show its receipts?",
    "",
    "two detours became directions.",
    "",
    "the research internship at NITK (jan–apr 2025) — video captioning, knowledge graphs — taught me that 'making a machine describe the world' is really 'making it structure the world'. structure has been the theme ever since.",
    "",
    "the current quest (EG india, since apr 2025) made it practical: an AI workflow that reads dense technical pricing documents and hands back structured data, fastapi services behind it, next.js frontends in front — and one legacy microsoft app that took 2–3 minutes to do its job, until it took under 10 seconds.",
    "",
    "the saxophone handles the rest: long notes, patience, debugging by ear.",
    "",
    "so, origin story? too many tabs open. every one of them asking 'what if i just tried it?'. at some point, trying became the story.",
].join("\n");

export const BRAIN_FILES: BrainFile[] = [
    {
        id: "tabs",
        name: "37_tabs_open.txt",
        icon: Globe,
        size: "37 rows",
        modified: "continuously",
        kind: "tabs",
        paragraphs: [],
    },
    {
        id: "overthink",
        name: "things_i_overthink.md",
        icon: FileText,
        size: "12 KB, and growing",
        modified: "mid-thought",
        kind: "static",
        paragraphs: [
            "- whether the solution i just wrote is the best solution, or just the first one that worked.",
            "- whether there is a cleaner architecture one folder over.",
            "- whether i should refactor, or leave it alone, or refactor the refactor.",
            "- whether this side project idea is 'the good kind of distraction' or 'the other kind'.",
            "- whether the tab count is a personality trait or a warning label. (verdict: both.)",
            "- whether 'done' means works, or works well, or works so well i rebuild it. currently: works.",
        ],
    },
    {
        id: "side-quest",
        name: "current_side_quest.txt",
        icon: Compass,
        size: "1 quest",
        modified: "whenever the last one ended",
        kind: "static",
        paragraphs: ["status: between side quests. the tabs always know first."],
        note: [
            "current: the agent shelf.",
            "",
            "in the last few weeks three of them left my laptop and landed on github — a RAG chatbot that cites its sources instead of improvising, a real-time gemini voice agent you can interrupt mid-sentence, and a self-hosted pull-request reviewer that leaves inline comments and a commit status.",
            "",
            "next in the queue: reading langchain's source properly (the fork is sitting in my repos like homework), evaluation for RAG — because 'it felt right' is not a metric — and making deployment boring enough that i stop treating it as the exciting part.",
        ].join("\n"),
    },
    {
        id: "origin",
        name: "origin_story.md",
        icon: FileText,
        size: "v1 — written, still growing",
        modified: "today",
        kind: "static",
        paragraphs: [
            "every OS boots from somewhere. this one booted from curiosity with too many tabs. the story so far:",
        ],
        note: ORIGIN_STORY,
    },
    {
        id: "me",
        name: "things_that_make_me_me.txt",
        icon: Heart,
        size: "surprisingly large",
        modified: "continuously",
        kind: "static",
        paragraphs: [
            "current interests, no particular order:",
            "- the saxophone. an instrument that flirts with jazz even in the practice room.",
            "- food, and watching mukbang videos. i enjoy eating far more than i enjoy cooking — a balanced diet of appreciation.",
            "- side quests. swimming, karate, carnatic music, bharatanatyam, western dance, drawing, badminton, yakshagana — childhood was a full curriculum.",
            "- toastmasters. turning 'public speaking' from a boss fight into a trainable skill.",
            "- curiosity with too many tabs open. professionally, it's called research.",
        ],
        note: "the line this file kept missing: i would rather ship something slightly embarrassing and learn from it than keep it perfect in a branch nobody can see. (see also: reading documentation for fun. strength or diagnosis — undecided.)",
    },
    {
        id: "more-questions",
        name: "more_questions_than_answers.txt",
        icon: FileQuestion,
        size: "all of them",
        modified: "right now",
        kind: "static",
        curiosityLocked: true,
        paragraphs: [
            "unlocked by curiosity (slider ≥ 70). obviously.",
            "",
            "- if RAG can cite sources, why do i still say 'trust me'?",
            "- can a model be 'mostly sure'? should it be allowed to sound sure?",
            "- what's the smallest thing i can automate that saves the most dignity?",
            "- if the saxophone had an API, what would its error messages sound like?",
        ],
    },
    {
        id: "found-everything",
        name: "YouFoundEverything.txt",
        icon: Trophy,
        size: "1 reward",
        modified: "the moment you earned it",
        kind: "final-egg",
        paragraphs: [
            "If you have made it this far, you're either exceptionally curious or very committed to avoiding your inbox. Either way, I think we'll get along. Thanks for exploring my little corner of the internet.",
            "",
            "— krithi",
        ],
    },
];
